import React, { useEffect, useState } from "react";
import { Button, message, Input, DatePicker, Select, Space, Typography } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined, SearchOutlined, EyeOutlined } from "@ant-design/icons";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getUserRole } from "../../utils/auth";
import SaleReportTempDrawer from "./SalesReportTempDrawer";
import InvoicePreview from "./InvoicePreview";

// Extend Day.js with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Title } = Typography;

export default function SalesReportTemp() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    storeName: null,
    fromDate: null,
    toDate: null,
    sellerName: null,
    customerName: null,
    employeeName: null,
    financialYear: null,
    search: null,
  });
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState({ role: "", editPermission: false });
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState(null);
  const navigate = useNavigate();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value || null }));
    setPaginationInfo((prev) => ({ ...prev, current: 1 }));
  };

  const fetchUsersAndStores = async () => {
    try {
      const [usersResponse, storesResponse] = await Promise.all([
        API.get("/users"),
        API.get("/stores"),
      ]);
      setUsers(usersResponse?.data || []);
      setStores(storesResponse?.data || []);

      const userData = JSON.parse(localStorage.getItem('userData')) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();
      setUserRole(userRoleValue);

      if (userRoleValue.role !== 'admin' && userStoreId) {
        const matchingStore = storesResponse.data.find((store) => store._id === userStoreId);
        if (matchingStore) {
          setFilters((prev) => ({ ...prev, storeName: matchingStore.name }));
        }
      }
    } catch (error) {
      console.error("Error fetching users or stores:", error);
      message.error("Failed to load users or stores.");
      setUsers([]);
      setStores([]);
    }
  };

  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1);
    let currentFinancialYearStart, currentFinancialYearEnd;
    if (currentDate < financialYearStart) {
      currentFinancialYearStart = currentDate.getFullYear() - 1;
      currentFinancialYearEnd = currentDate.getFullYear();
    } else {
      currentFinancialYearStart = currentDate.getFullYear();
      currentFinancialYearEnd = currentDate.getFullYear() + 1;
    }

    const financialYears = [];
    let startYear = 2024;
    let endYear = startYear + 1;
    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }
    return financialYears;
  };

  const getSales = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      setSales([]); // Clear previous sales to prevent stale data
      const { storeName, search, fromDate, toDate, customerName, sellerName, employeeName, financialYear } = filters;
      console.log('Fetching from endpoint: /api/sales/missing-purchase-price', { page, pageSize, filters });
      const response = await API.get("/sales/missing-purchase-price", {
        params: {
          page,
          limit: pageSize,
          storeName,
          search,
          customerName,
          employeeName,
          sellerName,
          fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : null,
          toDate: toDate ? dayjs(toDate).format('YYYY-MM-DD') : null,
          financialYear: financialYear || null,
        },
      });

      const salesData = Array.isArray(response?.data?.sales) ? response.data.sales : [];
      // Log fetched sales
      console.log('Fetched sales:', salesData.map(sale => ({
        _id: sale._id,
        invoiceId: sale.invoiceId,
        hasProductsPurchasePrice: sale.products.some(product => product.productsPurchasePrice != null),
        productsPurchasePrice: sale.products.map(p => p.productsPurchasePrice || 'Not Set'),
      })));
      // Warn if any sale has productsPurchasePrice
      if (salesData.some(sale => sale.products.every(product => product.productsPurchasePrice != null))) {
        console.warn('Warning: Some fetched sales have all products with productsPurchasePrice:', {
          sales: salesData
            .filter(sale => sale.products.every(product => product.productsPurchasePrice != null))
            .map(sale => ({
              _id: sale._id,
              invoiceId: sale.invoiceId,
              productsPurchasePrice: sale.products.map(p => p.productsPurchasePrice),
            })),
        });
      }
      setSales(salesData);
      setPaginationInfo({
        current: page,
        pageSize,
        total: response?.data?.pagination?.total || 0,
      });
    } catch (error) {
      console.error("Error fetching sales:", error);
      message.error("Failed to load sales without purchase price.");
      setSales([]);
      setPaginationInfo((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersAndStores();
  }, []);

  useEffect(() => {
    getSales(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.current, paginationInfo.pageSize]);

  const handlePaginationChange = (pagination) => {
    setPaginationInfo({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
    });
  };

  const handleSaveSale = async (updatedSale) => {
    setOpenDrawer(false);
    setSelectedSale(null);
    getSales(paginationInfo.current, paginationInfo.pageSize, filters);
  };

  const handleEditSale = (sale) => {
    setSelectedSale(sale);
    setOpenDrawer(true);
  };

  const handleDownload = async (data) => {
    try {
      const result = await API.get(`/sales/generate-invoice/${data?._id}`);
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${data?.invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      message.error("Failed to generate invoice.");
    }
  };

  const handlePreview = (sale) => {
    setSelectedInvoiceForPreview(sale);
    setIsPreviewModalVisible(true);
  };

  const handleDeleteSale = async () => {
    try {
      await API.delete(`/sales/${saleToDelete}`);
      message.success("Sale deleted successfully!");
      setSales((prev) => prev.filter((sale) => sale._id !== saleToDelete));
      setIsDeleteModalVisible(false);
      setSaleToDelete(null);
      getSales(paginationInfo.current, paginationInfo.pageSize, filters);
    } catch (error) {
      console.error("Error deleting sale:", error);
      message.error(error.response?.data?.message || "Failed to delete sale.");
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      width: 80,
      fixed: 'left',
      render: (_, __, index) =>
        ((paginationInfo.current || 1) - 1) * (paginationInfo.pageSize || 20) + index + 1,
    },
    {
      title: "Invoice No.",
      render: (_, sale) => sale?.invoiceId,
      key: "invoiceId",
      width: 180,
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleFilterChange('search', e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Invoice Date",
      key: "orderDate",
      width: 140,
      render: (_, sale) => dayjs(sale?.orderDate).format('DD-MM-YYYY'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys && selectedKeys.length > 0 ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])] : []}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")] : []);
            }}
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleFilterChange("fromDate", selectedKeys[0]);
                  handleFilterChange("toDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleFilterChange("fromDate", null);
                handleFilterChange("toDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Invoice Amount (₹)",
      key: "totalAmount",
      width: 160,
      render: (_, sale) => `₹ ${new Intl.NumberFormat('en-IN').format(sale?.totalAmount || 0)}`,
    },
    {
      title: "Paid (₹)",
      key: "paidAmount",
      width: 120,
      render: (_, sale) => `₹ ${new Intl.NumberFormat('en-IN').format(sale?.paidAmount || 0)}`,
    },
    {
      title: "Balance (₹)",
      key: "balance",
      width: 140,
      render: (_, sale) => (
        <div style={{ flexDirection: 'column', fontSize: '12px' }}>
          <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green", fontWeight: 'bold' }}>
            ₹ {new Intl.NumberFormat('en-IN').format(sale?.balanceAmount || 0)}
          </span>
          {sale?.balanceAmount > 0 && (
            <>
              <br />
              <span style={{ color: "gray", fontSize: '11px' }}>Paid: </span>
              <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green", fontSize: '11px' }}>
                ₹ {new Intl.NumberFormat('en-IN').format(sale?.balancePaid || 0)}
              </span>
              <br />
              <span style={{ color: "gray", fontSize: '11px' }}>Remaining: </span>
              <span style={{ color: sale?.balanceAmount - sale?.balancePaid > 0 ? "red" : "green", fontSize: '11px' }}>
                ₹ {new Intl.NumberFormat('en-IN').format((sale?.balanceAmount - sale?.balancePaid) || 0)}
              </span>
            </>
          )}
        </div>
      ),
    },
    {
      title: "Pay Mode",
      key: "paymentMethod",
      width: 90,
      render: (_, sale) => (
        <span style={{ 
          fontWeight: 'bold', 
          color: sale?.paymentMethod === 'CASH' ? '#52c41a' : '#1890ff',
          fontSize: '12px'
        }}>
          {sale?.paymentMethod?.toUpperCase() || '--'}
        </span>
      ),
    },
    {
      title: "Customer",
      key: "customerName",
      width: 180,
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", fontSize: "12px" }}>
            {sale?.customerId?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {sale?.customerId?.phone || "---"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Customer"
          onChange={(e) => handleFilterChange("customerName", e.target.value)}
          value={filters.customerName}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Employee",
      key: "employeeName",
      width: 150,
      render: (_, sale) => (
        <div>
          <div style={{ color: 'orange', fontWeight: "bold", fontSize: "12px" }}>
            {sale?.employeeId?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {sale?.employeeId?.phone || "---"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Employee"
          onChange={(e) => handleFilterChange("employeeName", e.target.value)}
          value={filters.employeeName}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Store",
      key: "storeName",
      width: 120,
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", color: '#1591EA', fontSize: "12px" }}>
            {sale?.storeId?.name || "N/A"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Select
          style={{ width: 200 }}
          placeholder="Search Store"
          onChange={(value) => handleFilterChange("storeName", value)}
          value={filters.storeName}
          allowClear
          disabled={userRole.role !== 'admin'}
        >
          {stores.map((store) => (
            <Select.Option key={store._id} value={store.name}>
              {store.name}
            </Select.Option>
          ))}
        </Select>
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => {
        let color;
        if (status === "pending") color = "yellow";
        else if (status === "completed") color = "green";
        else if (status === "cancelled") color = "red";
        else if (status === "replace") color = "blue";
        return (
          <span style={{ color, fontWeight: "bold" }}>
            {status ? status.charAt(0).toUpperCase() + status.slice(1) : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Purchase Price",
      key: "productsPurchasePrice",
      width: 150,
      render: (_, sale) => (
        sale.products.map(p => p.productsPurchasePrice != null ? `₹${new Intl.NumberFormat('en-IN').format(p.productsPurchasePrice)}` : 'Not Set').join(', ')
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: 'right',
      render: (_, sale) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handlePreview(sale)}
            size="small"
            style={{ color: '#1890ff', borderColor: '#1890ff' }}
            title="Preview Invoice"
          />
          {userRole.editPermission && (
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(sale)}
              size="small"
              style={{ color: '#52c41a', borderColor: '#52c41a' }}
              title="Download Invoice"
            />
          )}
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditSale(sale)}
            size="small"
            style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
            title="Edit Sale"
          />
          {userRole.editPermission && (
            <Button
              icon={<DeleteOutlined />}
              onClick={() => {
                setSaleToDelete(sale._id);
                setIsDeleteModalVisible(true);
              }}
              size="small"
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
              title="Delete Sale"
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <Title level={3}>Sales Without Purchase Price</Title>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <Button type="primary" onClick={() => navigate("/saleForm")}>
          Add Sales
        </Button>

        <Space wrap align="end" style={{ padding: '10px 20px' }}>
          <Select
            style={{ width: 200 }}
            placeholder="Select Seller"
            allowClear
            onChange={(value) => handleFilterChange("sellerName", value)}
            value={filters.sellerName}
          >
            {users.map((user) => (
              <Select.Option key={user._id} value={user.name}>
                {user.name}
              </Select.Option>
            ))}
          </Select>

          <Select
            style={{ width: 200 }}
            placeholder="Select Store"
            allowClear
            onChange={(value) => handleFilterChange("storeName", value)}
            value={filters.storeName}
            disabled={userRole.role !== 'admin'}
          >
            {stores.map((store) => (
              <Select.Option key={store._id} value={store.name}>
                {store.name}
              </Select.Option>
            ))}
          </Select>

          <DatePicker.RangePicker
            value={filters.fromDate && filters.toDate ? [dayjs(filters.fromDate), dayjs(filters.toDate)] : []}
            onChange={(dates) => {
              if (dates && dates.length === 2) {
                if (dates[0].isAfter(dates[1])) {
                  message.error("From date cannot be after to date.");
                  return;
                }
                handleFilterChange("fromDate", dates[0].format("YYYY-MM-DD"));
                handleFilterChange("toDate", dates[1].format("YYYY-MM-DD"));
              } else {
                handleFilterChange("fromDate", null);
                handleFilterChange("toDate", null);
              }
            }}
            format="YYYY-MM-DD"
            allowClear
          />

          <Select
            style={{ width: 150 }}
            placeholder="Select Financial Year"
            allowClear
            onChange={(value) => handleFilterChange("financialYear", value)}
            value={filters.financialYear}
          >
            {generateFinancialYears().map((year) => (
              <Select.Option key={year} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>

          <Button
            onClick={() => {
              setFilters({
                storeName: userRole.role !== 'admin' ? filters.storeName : null,
                fromDate: null,
                toDate: null,
                sellerName: null,
                customerName: null,
                employeeName: null,
                financialYear: null,
                search: null,
              });
              setPaginationInfo((prev) => ({ ...prev, current: 1 }));
            }}
          >
            Reset Filters
          </Button>
        </Space>
      </Space>

      <div style={{ 
        overflowX: 'auto',
        border: '1px solid #f0f0f0',
        borderRadius: '8px',
        background: '#fff'
      }}>
        <CustomTable
          columns={columns}
          dataSource={sales}
          onPaginationChange={handlePaginationChange}
          loading={loading}
          pagination={{
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          style={{ 
            padding: '0 20px',
            minWidth: '1380px' // Ensure minimum width for all columns (includes Purchase Price column)
          }}
          scroll={{ x: 1380 }} // Enable horizontal scroll
          locale={{ emptyText: 'No sales found without purchase price.' }}
        />
      </div>

      <SaleReportTempDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        data={selectedSale}
        onSave={handleSaveSale}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteSale}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setSaleToDelete(null);
        }}
        title="Confirm Deletion"
        content="Are you sure you want to delete this sale? This action cannot be undone."
      />

      <InvoicePreview
        visible={isPreviewModalVisible}
        onClose={() => setIsPreviewModalVisible(false)}
        invoiceData={selectedInvoiceForPreview}
      />
    </div>
  );
}